import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BellRinging, X } from "@phosphor-icons/react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Prompts the user to enable notifications so incoming calls ring their phone
// even when the app is closed. Hidden once granted, denied, or dismissed.
export const PushNudge = () => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    if (!supported) return;
    if (Notification.permission === "granted" || Notification.permission === "denied") return;
    if (localStorage.getItem("iyeneh_pushnudge_dismissed")) return;
    setShow(true);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const p = await Notification.requestPermission();
      if (p !== "granted") { toast("You can enable call alerts later in your browser settings"); setShow(false); return; }
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.key) { setShow(false); return; }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.key) });
      await api.post("/push/subscribe", { subscription: sub.toJSON() });
      toast.success("Call alerts on — your phone will ring even when the app is closed");
      setShow(false);
    } catch {
      toast.error("Couldn't enable notifications");
    } finally { setBusy(false); }
  };

  const dismiss = () => { localStorage.setItem("iyeneh_pushnudge_dismissed", "1"); setShow(false); };

  if (!show) return null;
  return (
    <div data-testid="push-nudge" className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3">
      <BellRinging size={22} weight="fill" className="shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Never miss a call</p>
        <p className="text-xs text-muted-foreground">Turn on notifications so calls ring your phone even when iYeneh is closed.</p>
      </div>
      <button data-testid="push-nudge-enable" onClick={enable} disabled={busy}
        className="whitespace-nowrap rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-60">
        {busy ? "Enabling…" : "Turn on"}
      </button>
      <button data-testid="push-nudge-dismiss" onClick={dismiss} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
    </div>
  );
};

export default PushNudge;
